import { Component, OnInit, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../services/auth.service';
import { MusicSyncService } from '../../services/music-sync.service';
import { APP_CONSTANTS } from '../../constants/app.constants';
import { environment } from '../../../environments/environment';

@Component({
  selector: 'app-auth-callback',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="callback-container">
      <div class="callback-content">
        <div class="loading-spinner"></div>
        <h2>{{ loadingMessage }}</h2>
        <p class="callback-status">{{ statusMessage }}</p>
        <div class="error-message" *ngIf="errorMessage">
          <p>{{ errorMessage }}</p>
          <button class="retry-btn" (click)="goToDashboard()">
            Back to Dashboard
          </button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .callback-container {
      min-height: 100vh;
      background: linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%);
      color: #e2e8f0;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
    }

    .callback-content {
      text-align: center;
      max-width: 400px;
      padding: 2rem;
    }

    .loading-spinner {
      width: 50px;
      height: 50px;
      border: 4px solid rgba(120, 119, 198, 0.3);
      border-top: 4px solid #7877c6;
      border-radius: 50%;
      animation: spin 1s linear infinite;
      margin: 0 auto 2rem;
    }

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      color: #ffffff;
      margin-bottom: 1rem;
    }

    .callback-status {
      color: #a0aec0;
      margin-bottom: 2rem;
      line-height: 1.6;
    }

    .error-message {
      background: rgba(239, 68, 68, 0.1);
      border: 1px solid rgba(239, 68, 68, 0.3);
      border-radius: 0.5rem;
      padding: 1rem;
      color: #ef4444;

      .retry-btn {
        background: #7877c6;
        border: none;
        color: #ffffff;
        padding: 0.75rem 1.5rem;
        border-radius: 0.5rem;
        font-weight: 600;
        cursor: pointer;
        margin-top: 1rem;
        transition: all 0.2s ease;

        &:hover {
          background: #9896f0;
        }
      }
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
  `]
})
export class AuthCallbackComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private authService = inject(AuthService);
  private musicSync = inject(MusicSyncService);

  loadingMessage = 'Processing authentication...';
  statusMessage = 'Please wait while we validate your connection.';
  errorMessage = '';

  ngOnInit(): void {
    // Debug: Log all URL information
    console.log('🐛 AuthCallback Debug Info:');
    console.log('- Current URL:', window.location.href);
    console.log('- Route params:', this.route.snapshot.params);
    console.log('- Query params:', this.route.snapshot.queryParams);
    console.log('- URL segments:', this.route.snapshot.url);
    console.log('- Full route data:', this.route.snapshot);

    this.handleOAuthCallback();
  }

  private async handleOAuthCallback(): Promise<void> {
    try {
      const queryParams = this.route.snapshot.queryParams;
      const currentUrl = this.route.snapshot.url.join('/');

      // Debug logging
      console.log('🔄 AuthCallback Debug:');
      console.log('- URL:', currentUrl);
      console.log('- Query params:', queryParams);

      // Detect provider from URL or query params
      let provider = queryParams['provider'] as 'spotify' | 'discogs';
      if (!provider) {
        if (currentUrl.includes('spotify')) {
          provider = 'spotify';
        } else if (currentUrl.includes('discogs')) {
          provider = 'discogs';
        }
      }

      // Validate state for Spotify
      if (provider === 'spotify') {
        const savedState = sessionStorage.getItem('spotify_auth_state');
        const receivedState = queryParams['state'];

        console.log('🔍 AuthCallback: State validation:', {
          saved: savedState,
          received: receivedState,
          hasSavedState: !!savedState,
          hasReceivedState: !!receivedState
        });

        // If we have a saved state but no received state, this might be a backend issue
        // We'll allow the connection to proceed but log a warning
        if (savedState && !receivedState) {
          console.warn('⚠️ AuthCallback: Backend did not return state parameter, but proceeding with connection');
          console.warn('⚠️ AuthCallback: This might indicate a backend configuration issue');
        } else if (savedState !== receivedState) {
          console.error('❌ AuthCallback: State mismatch', { saved: savedState, received: receivedState });
          this.handleError('Invalid authentication state. Please try again.');
          return;
        }

        // Clean up state
        sessionStorage.removeItem('spotify_auth_state');
      }

      let token = queryParams['token'] || queryParams['accessToken'] || queryParams['oauth_token'] || queryParams['code'];
      const error = queryParams['error'];
      const verifier = queryParams['oauth_verifier'];

      console.log('🔍 AuthCallback: Token extraction:', {
        token: token ? `${token.substring(0, 20)}...` : null,
        tokenLength: token?.length,
        hasToken: !!token,
        allTokenParams: {
          token: queryParams['token'],
          accessToken: queryParams['accessToken'],
          oauth_token: queryParams['oauth_token'],
          code: queryParams['code']
        }
      });

      // Handle OAuth errors
      if (error) {
        console.error('❌ AuthCallback: OAuth error:', error);
        this.handleError(`Authentication error: ${error}`);
        return;
      }

      // Validate required parameters
      if (!provider || !token) {
        console.error('❌ AuthCallback: Missing parameters:', { provider, token });
        this.handleError('Invalid authentication parameters');
        return;
      }

      // Update loading message
      const providerName = provider === 'spotify' ? 'Spotify' : 'Discogs';
      this.loadingMessage = `🎵 Connecting ${providerName}...`;
      this.statusMessage = `Syncing your ${providerName} music...`;

      try {
        // Handle the OAuth callback
        await this.musicSync.handleOAuthCallback(provider, token, verifier);

        console.log('✅ AuthCallback: OAuth callback completed successfully');

        // Short delay before redirect
        await new Promise(resolve => setTimeout(resolve, 1500));

        // Redirect to dashboard
        this.router.navigate([APP_CONSTANTS.ROUTES.DASHBOARD], {
          queryParams: { connected: provider }
        });
      } catch (error: any) {
        console.error('❌ AuthCallback: Error in OAuth callback:', error);
        this.handleError(error.message || 'Failed to complete authentication');
      }
    } catch (error: any) {
      console.error('❌ AuthCallback: Unexpected error:', error);
      this.handleError('An unexpected error occurred');
    }
  }

  private handleError(message: string): void {
    this.errorMessage = message;
    this.loadingMessage = 'Authentication Failed';
    this.statusMessage = 'Please try again or contact support if the issue persists.';
  }

  goToDashboard(): void {
    this.router.navigate([APP_CONSTANTS.ROUTES.DASHBOARD]);
  }
}
